import {Component, OnInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import { AppService } from '@irb/app.service';
import { IrbClientsService } from '../irb-clients.service';
import { IrbClientsEvents, IrbCreatePortfolioEvents, IrbAppEvents } from '@irb-common/events';
import { IrbCreatePortfolioService } from '@irb/irb-create-portfolio/irb-create-portfolio.service';
import { UtilityService } from '@irb-common/services/utility.service';
import {IAlertDialogData} from "@irb/app.model";

@Component({
  standalone: false,
  selector: 'irb-import-accounts-to-portfolio',
  templateUrl: './import-accounts-to-portfolio.component.html',
  styleUrls: ['./import-accounts-to-portfolio.component.scss', '../../irb-common/styles/dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ImportAccountsToPortfolioComponent implements OnInit, OnDestroy {
  file: any;
  hasFile: boolean = false;
  isValidFile: boolean = false;
  invalidFileErrorHeader: string;
  invalidFileErrorMessage: string;
  errorMessage: string;
  progressBarValue = 0;
  showProgressBar = false;
  updateStatusCount = 0;
  progressTimer: any = null;
  isUiTest = false;

  constructor(
      public dialogRef: MatDialogRef<ImportAccountsToPortfolioComponent>,
      public as: AppService,
      public cs: IrbClientsService,
      private cps: IrbCreatePortfolioService,
      private util: UtilityService
  ) {
  }

  ngOnInit() {
  }

  close() {
    this.showProgressBar = false;
    this.dialogRef.close();
  }

  onFileChange(event: any) {
    this.file = event.target.files[0];
    this.hasFile = this.isValidFile = true;
    if (!this.file.name.endsWith('.csv')) {
      this.isValidFile = false;
      this.invalidFileErrorHeader = "Invalid file format.";
      this.invalidFileErrorMessage = "Extension type must be \".csv\"";
    }
    if (this.as.maxFileUploadSizeExceeded(this.file)) {
      this.triggerMaxFileUploadSizeExceededError();
    }
  }

  private triggerMaxFileUploadSizeExceededError() {
    this.isValidFile = false;
    this.invalidFileErrorHeader = "Invalid file size.";
    this.invalidFileErrorMessage = "File size exceeds 50MB limit.";
  }

  submitFile() {
    const f = new FormData();
    f.append('ImportAccountCash0', this.file, this.file.name);

    this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
    this.cs.validateImportAccountToPortfolioAssignmentsAsync(f).subscribe((response: any) => {
      //this.finishValidatingImportAcctToPortfolio(response)
      if (this.progressTimer) clearTimeout(this.progressTimer);
      if (response.responseStatus === "SUCCESS" && response.responseUtilityMap)
        this.validateImportStatusCheckIntervalStart(response.responseUtilityMap.statusId);
    }, () => {
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    });
  }

  validateImportStatusCheckIntervalStart(statusId: any) {
    this.cs.validateImportAccountToPortfolioStatus(statusId).subscribe((statusResponse: any) => {
      if (statusResponse.responseStatus === "SUCCESS") {
        const responseData = statusResponse.responseData;
        let reqStatus = 'FAILED';
        if (statusResponse.responseUtilityMap && statusResponse.responseUtilityMap.reqStatus) {
          reqStatus = statusResponse.responseUtilityMap.reqStatus.trim().toUpperCase();
        }

        if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
          let timeout = this.isUiTest ? 0 : 1500;
          this.progressTimer = setTimeout(() => {
            this.validateImportStatusCheckIntervalStart(statusId);
          }, timeout);
        }

        if (reqStatus === 'COMPLETED') {
          this.finishValidatingImportAcctToPortfolio(responseData);
          if (this.progressTimer) clearTimeout(this.progressTimer);
          this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }

        if (reqStatus === 'FAILED') {
          this.actionValidationStatusCheckFailed();
        }
      } else {
        this.actionValidationStatusCheckFailed();
      }
    }, () => {
      this.close();
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
      if (this.progressTimer) clearTimeout(this.progressTimer);
    });
  }
  actionValidationStatusCheckFailed() {
    this.close();
    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    if (this.progressTimer) clearTimeout(this.progressTimer);
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dType: 'ERROR',
      dTitle: '',
      dProceedBtnLabel: '',
      msg: 'An error occurred processing your request.',
      onProceed: () => {
      }
    } as IAlertDialogData);
  }

  finishValidatingImportAcctToPortfolio(res: string) {
    const response = JSON.parse(res.substring(res.indexOf('<textarea>') + 10, res.indexOf('</textarea>')));

    if (response.STALEDATAEXCEPTION && response.STALEDATAEXCEPTION === true) {
      this.errorMessage = 'You are attempting to import items which were just modified by someone else. Please try your import again.';
      return;
    }

    if (response.status && response.status === "fail") {
      if (response.message) {
        this.errorMessage = response.message;
      } else {
        this.isValidFile = false;
      }
      return;
    }

    const hasValidRecords = (response.validRecords && response.validRecords === true);
    const hasInvalidRecords = (response.invalidRecords && response.invalidRecords.length > 0);


    if (hasValidRecords && !hasInvalidRecords) {
      this.as.fireEvent(IrbCreatePortfolioEvents.SHOW_PORTFOLIO_IMPORT_HOLDINGS, {
        portfolioAccounts: response.accountVeoList,
        isAltWorkflow: true,
        finishButtonLabel: "Finish importing assignments",
        finishButtonCallback: (() => {
          this.finishButtonCallbackFn(response);
        })
      });
    } else if (!hasValidRecords && !hasInvalidRecords) {
      this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
        severity: 'warn',
        summary: 'Import Accounts to Portfolio',
        detail: 'The import completed without any assignments being imported.'
      });
    } else if (hasInvalidRecords) {
      this.as.fireEvent(IrbClientsEvents.SHOW_IMPORT_ACCOUNTS_TO_PORTFOLIO_RESULT, response);
    }

    this.close();
  }

  finishButtonCallbackFn(response: any) {
    this.as.fireEvent(IrbAppEvents.SHOW_WAIT);

    this.cps.createPFforUnassignedAccountImportAsync(response.acctPortImportDTO).subscribe((response: any) => {
      this.updateStatusCount = 0;
      if (this.progressTimer) clearTimeout(this.progressTimer);
      this.showProgressBar = true;
      this.updateStatusCount = 0;
      if (response.responseUtilityMap)
        this.actionStatusCheckIntervalStart(response.responseUtilityMap.statusId);
    }, () => {
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    });
  }

  updateProgressBarStatus(response: any) {
    const cnt: any = response.failedRecCount + response.successRecCount;
    const rcnt: any = response.recievedCount;
    if (rcnt > 0) {
      const progressPercentage = Math.floor((cnt * 100) / rcnt);
      this.progressBarValue = progressPercentage;
    }
    let timeout = (this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
    if (this.isUiTest)
      timeout = 0;
    this.updateStatusCount++;
    this.progressTimer = setTimeout(() => {
      this.actionStatusCheckIntervalStart(response.statusId);
    }, timeout);
  }

  actionStatusCheckIntervalStart(actionStatusId: any) {
    this.cps.actionStatusCheck(actionStatusId).subscribe((statusResponse: any) => {
      if (statusResponse.responseStatus === "SUCCESS") {
        const response = statusResponse.responseData;
        const reqStatus = response.reqStatus.trim().toUpperCase();
        if (reqStatus === 'FAILED') {
          if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION')
            this.showStaleDataDialog();
          else
            this.actionStatusCheckFailed(response);
        }

        if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
          this.updateProgressBarStatus(response);
        }
        if (reqStatus === 'COMPLETED') {
          this.close();
          if(this.progressTimer) clearTimeout(this.progressTimer);
           this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
          this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
          this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: 'success',
            summary: 'Import Accounts to Portfolio',
            detail: response.recievedCount + " accounts were removed from this list and assigned to portfolios."
          });
        }
      } else {
        this.close();
        if(this.progressTimer) clearTimeout(this.progressTimer);
      }
    }, () => {
      this.close();
      if(this.progressTimer) clearTimeout(this.progressTimer);
    });
  }

  actionStatusCheckFailed(response: any) {
    this.close();
     this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dType: 'ERROR',
      dTitle: '',
      dProceedBtnLabel: '',
      msg: response.failMsg,
      onProceed: () => {
      }
    });
  }

  showStaleDataDialog() {
    this.close();
     this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
    this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
      dTitle: 'Refresh Required',
      dProceedBtnLabel: 'Refresh',
      msg: "You are attempting to create or update accounts that was just modified by someone else. To see the updated accounts, you need to refresh the page.",
      onProceed: () => {
        this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
      }
    });
  }

  ngOnDestroy() {
    if(this.progressTimer) clearTimeout(this.progressTimer);
  }
}
